import { Fragment, useMemo } from 'react';
import { func, oneOf, bool, string } from 'prop-types';
import styled, { css } from 'styled-components/macro';
import { min, max } from 'd3-array';
import flatten from 'lodash.flatten';
import delve from 'dlv';

import getEntityKey from 'helpers/getEntityKey';

import entitiesPropType from './prop-types/entities';
import AnimatedPath from '../AnimatedPath/AnimatedPath';
import PathWithFilledGaps from '../PathWithFilledGaps';
import MissingDataTrendLines from '../MissingDataTrendLines';
import AnimatedCircle from '../AnimatedCircle/AnimatedCircle';

const radius = 3;

const LineCapStyles = css`
  stroke: none;
`;

const AnimatedGradientPath = styled(AnimatedPath)`
  fill: transparent;
  stroke-linejoin: round;
`;

const StyledTerminiCircle = styled(AnimatedCircle)`
  ${LineCapStyles};
`;

const StyledOriginCircle = styled(AnimatedCircle)`
  ${LineCapStyles};
`;

/**
 * Renders a small dot for each value in the data.
 *
 * @param {object} props React props
 * @param {array} props.trends Array of trend data
 * @param {string} props.dataKey The key at which to find the data in each trend point ('value' or 'position')
 * @param {function} props.x Configured d3.js time scale for the x-axis
 * @param {function} props.y Configured d3.js linear scale for the y-axis
 * @param {function} props.colorScale A d3.js color scale
 *
 * @returns {Array} An array of SVG circle elements
 */
function Ticks(props) {
  const { trends, x, y, colorScale, dataKey } = props;

  return flatten(
    trends.map((trend) =>
      trend.data.map((d, i) => {
        const adjacentsAreNull =
          delve(trend, ['data', i - 1, dataKey], undefined) === null &&
          delve(trend, ['data', i + 1, dataKey], undefined) === null;

        return delve(d, dataKey) === null ? null : (
          <AnimatedCircle
            key={`tick-${getEntityKey(trend, d)}-${d.periodKey}`}
            fill={colorScale(getEntityKey(trend))}
            cxPosition={x(d.date)}
            cyPosition={y(delve(d, dataKey))}
            r={adjacentsAreNull ? radius : 1}
          />
        );
      })
    )
  );
}

/**
 * Renders linear chart lines for each entity in the data.
 *
 * @param {object} props React props
 * @param {array} props.trends Trend data
 * @param {function} props.colorScale A d3.js color scale
 * @param {string} props.dataKey The key at which to find the data in each trend point ('value' or 'position')
 * @param {function} props.path Configured d3.js path generator
 * @param {function} props.x Configured d3.js time scale for the x-axis
 * @param {number} props.width The width of the graph these lines are rendered in
 * @param {number} props.height The height of the graph these lines are rendered in
 *
 * @returns {React.Component}
 */
export function Lines(props) {
  const {
    trends,
    colorScale,
    dataKey,
    path,
    x,
    width,
    height,
    hasDashedGaps,
    isTimeBoxing,
  } = props;

  return trends.map((trend) => {
    return (
      <PathWithFilledGaps
        key={`line-path-${getEntityKey(trend)}`}
        trend={trend}
        path={path}
        x={x}
        width={width}
        height={height}
        showGapLines={
          hasDashedGaps !== undefined ? hasDashedGaps : dataKey !== 'position'
        }
        valueKey={dataKey}
        stroke={colorScale(getEntityKey(trend))}
        isTimeBoxing={isTimeBoxing}
      />
    );
  });
}

/**
 * Render linear chart line gradients for each entity in the data.
 * We will generate a random hash for the linear-gradient and url reference to it on the animated path. Safari is having trouble to deal with updates in svg elements therefore we need to add a hash to ids.
 *
 * @param {object} props React props
 * @param {array} props.trends Trend data
 * @param {function} props.colorScale A d3.js color scale
 * @param {function} props.gradient Configured d3.js area generator
 * @param {string} props.customKey additional key

 * @returns {Array} An array of React.Components
 */
export function Gradients(props) {
  const { trends, gradient, colorScale, customKey } = props;

  return trends.map((trend) => {
    const key = customKey ? trend[customKey] : getEntityKey(trend);

    return (
      <Fragment key={`gradient-${key}$`}>
        <defs>
          <linearGradient
            id={`gradient-fill-${key}`}
            x1={0}
            x2={0}
            y1={0}
            y2={1}
          >
            <stop offset="0%" stopColor={colorScale(key)} stopOpacity="0.15" />
            <stop offset="75%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        <AnimatedGradientPath
          pathString={gradient(trend.data)}
          style={{ fill: `url(#gradient-fill-${key})` }}
        />
      </Fragment>
    );
  });
}

/**
 * Renders a dot for the beginning of the chartline, but only if the earliest datapoint in the chart has a value.
 *
 * @param {object} props React props
 * @param {array} props.trends Trend data
 * @param {string} props.dataKey The key at which to find the data in each trend point ('value' or 'position')
 * @param {function} props.x Configured d3.js time scale for the x-axis
 * @param {function} props.y Configured d3.js linear scale for the y-axis
 * @param {function} props.colorScale A d3.js color scale
 *
 * @returns {Array} An array of React.Components.
 */
function Origins(props) {
  const { trends, x, y, colorScale, dataKey } = props;

  /**
   * The originating points of each line
   */
  const originTrends = trends.filter(
    (trend) => trend.data && trend.data.length > 0
  );
  const origins = useMemo(() => {
    return originTrends.map((trend) => {
      const minDate = min(trend.data, (d) => d.date);
      return {
        ...trend,
        data: trend.data.find((d) => d.date === minDate),
      };
    });
  }, [originTrends]);

  return origins.map((origin) =>
    delve(origin.data, dataKey) === null ? null : (
      <StyledOriginCircle
        key={`origin-${getEntityKey(origin)}`}
        cxPosition={x(origin.data.date)}
        cyPosition={y(delve(origin.data, dataKey))}
        fill={colorScale(getEntityKey(origin))}
        r={radius}
      />
    )
  );
}

/**
 * Renders a dot for the end (terminus) of each chartline, if it has a value.
 *
 * @param {object} props React props
 * @param {array} props.trends Trend data
 * @param {string} props.dataKey The key at which to find the data in each trend point ('value' or 'position')
 * @param {function} props.x Configured d3.js time scale for the x-axis
 * @param {function} props.y Configured d3.js linear scale for the y-axis
 * @param {function} props.colorScale A d3.js color scale
 *
 * @returns {Array} An array of React.Components.
 */
function Termini(props) {
  const { trends, x, y, colorScale, dataKey } = props;

  /**
   * The terminating points of each trend
   */
  const originTrends = trends.filter(
    (trend) => trend.data && trend.data.length > 0
  );
  const termini = useMemo(() => {
    return originTrends.map((trend) => {
      const maxDate = max(trend.data, (d) => d.date);

      return {
        ...trend,
        data: trend.data.find((d) => d.date === maxDate),
      };
    });
  }, [originTrends]);

  return termini.map((terminus) =>
    delve(terminus.data, dataKey) === null ? null : (
      <StyledTerminiCircle
        key={`terminus-${getEntityKey(terminus)}`}
        cxPosition={x(terminus.data.date)}
        cyPosition={y(delve(terminus.data, dataKey))}
        fill={colorScale(getEntityKey(terminus))}
        r={radius}
      />
    )
  );
}

/**
 * Renders all lines in the chart and the other indicators of data the LineChart requires.
 *
 * @export
 * @param {object} props React props
 * @param {string} props.dataType The type of number displayed ('number' or 'position')
 * @param {string} props.dataKey The key at which to find the data in each trend point ('value' or 'position')
 * @param {array} props.trends Trend data
 * @param {boolean} props.fillFromLastNonNullValue Should the displayed data be filled with an extended line from the last non null value onwards?
 * @param {function} props.colorScale A d3.js color scale
 * @param {function} props.gradient Configured d3.js area generator
 * @param {function} props.path Configured d3.js path generator
 * @param {number} props.width The width of the graph these lines are rendered in
 * @param {number} props.height The height of the graph these lines are rendered in
 * @param {function} props.x Configured d3.js time scale for the x-axis
 * @param {function} props.y Configured d3.js linear scale for the y-axis
 * @returns {React.Component}
 */
export default function ChartLines(props) {
  const {
    dataType,
    fillFromLastNonNullValue,
    colorScale,
    trends,
    path,
    dataKey,
  } = props;

  return (
    <>
      <Ticks {...props} />
      {fillFromLastNonNullValue && (
        <MissingDataTrendLines
          colorScale={colorScale}
          dataKey={dataKey}
          trends={trends}
          path={path}
        />
      )}
      <Lines {...props} />
      {dataType === 'number' && <Gradients {...props} />}
      <Origins {...props} />
      <Termini {...props} />
    </>
  );
}

ChartLines.defaultProps = {
  dataKey: 'value',
};

ChartLines.propTypes = {
  colorScale: func.isRequired,
  dataKey: string,
  dataType: oneOf(['position', 'social', 'number']),
  fillFromLastNonNullValue: bool,
  gradient: func.isRequired,
  path: func.isRequired,
  trends: entitiesPropType.isRequired,
  x: func.isRequired,
  y: func.isRequired,
};
